import { Scrim } from "@/components/ui/Scrim";
import { Badge, Button, Card, Heading, Icon, MotionButton, Surface, Text } from "@/components/ui";
import { useModalLayer } from "@/contexts/ModalContext";
import {
  RAIL_CTA_REM,
  RAIL_MICRO_REM,
  RAIL_NAME_REM,
  RAIL_PAD_REM,
  STOREFRONT_ROUNDED_PX,
  type StorefrontSizeRounded,
  type StorefrontSizeToken,
} from "@/domain/storefront-appearance";
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll";
import type { BenefitShowcaseCard, BenefitShowcaseKind } from "@/lib/benefit-showcase";
import {
  formatCouponAvailabilityHint,
  isCouponAvailableNow,
} from "@/lib/coupon-availability";
import { ArrowsOutSimple, Close, Coins, Gift, Repeat, ShoppingBag, type IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { DURATION, EASE, MORPH_SHADOW, TRANSITION } from "@/lib/motion";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, useReducedMotion, type Transition } from "motion/react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";

const MotionSurface = motion.create(Surface);

const KIND_ICON: Record<BenefitShowcaseKind, IconGlyph> = {
  points: Coins,
  cartThreshold: ShoppingBag,
  itemPromo: Gift,
  recurrence: Repeat,
};

/** Icon chip diameter in rem per section size; the glyph is 55% of it. */
const ICON_CHIP_REM: Record<StorefrontSizeToken, number> = {
  compact: 2,
  medium: 2.5,
  large: 3,
};

/** Body lines a collapsed card shows before it clamps and offers the spotlight. */
const COLLAPSED_BODY_LINES = 3;

/** Title lines a collapsed card shows before it clamps. */
const COLLAPSED_TITLE_LINES = 2;

/** Spring shared by the grow and the shrink back, so both read as one gesture. */
const MORPH_TRANSITION: Transition = { type: "spring", bounce: 0.16, duration: DURATION.emphasis };

/** Upper bound for the shrink back when the layout animation never reports completion. */
const LEAVE_FALLBACK_MS = 900;

/** Clamp fade coming back while the card shrinks, paced to land with the morph. */
const FADE_RETURN: Transition = { duration: DURATION.entrance, ease: EASE.standard };

/** Clamp fade leaving as the card grows. */
const FADE_LEAVE: Transition = TRANSITION.exit;

/** Opacity of a card whose weekly window is closed now. */
const UNAVAILABLE_OPACITY = 0.6;

const MotionBackdrop = motion.create(Scrim);

interface BenefitCardStyleProps {
  card: BenefitShowcaseCard;
  size: StorefrontSizeToken;
  rounded: StorefrontSizeRounded;
}

/** Near-instant tween used instead of the morph when the user prefers reduced motion. */
const REDUCED_MOTION: Transition = { duration: DURATION.instant };

function lineClamp(lines: number): CSSProperties {
  return {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: lines,
    overflow: "hidden",
  };
}

function cardSurface(size: StorefrontSizeToken, rounded: StorefrontSizeRounded): CSSProperties {
  return {
    padding: `${RAIL_PAD_REM[size] * 1.6}rem`,
    borderRadius: `${STOREFRONT_ROUNDED_PX[rounded]}px`,
  };
}

function useAvailability(card: BenefitShowcaseCard) {
  return {
    availableNow: isCouponAvailableNow(card.availability ?? undefined),
    availabilityHint: formatCouponAvailabilityHint(card.availability ?? undefined),
  };
}

function BenefitIconChip({ kind, size }: { kind: BenefitShowcaseKind; size: StorefrontSizeToken }) {
  const Glyph = KIND_ICON[kind];
  const chipRem = ICON_CHIP_REM[size];
  return (
    <span
      aria-hidden="true"
      className="flex shrink-0 items-center justify-center rounded-full bg-secondary/10 text-secondary"
      style={{ width: `${chipRem}rem`, height: `${chipRem}rem` }}
    >
      <Icon as={Glyph} weight="fill" tone="secondary" size="custom" customSize={chipRem * 0.55} />
    </span>
  );
}

interface BenefitChipsProps {
  card: BenefitShowcaseCard;
  size: StorefrontSizeToken;
  layoutTransition?: Transition;
}

function BenefitChips({ card, size, layoutTransition }: BenefitChipsProps) {
  const { availableNow, availabilityHint } = useAvailability(card);
  if (!availabilityHint && !card.footnote) return null;

  return (
    <motion.div
      layout={layoutTransition ? "position" : false}
      transition={layoutTransition}
      className="mt-auto flex flex-wrap gap-1.5 pt-1"
      style={{ fontSize: `${RAIL_MICRO_REM[size] * 1.1}rem` }}
    >
      {availabilityHint && (
        <Badge
          size="inherit"
          uppercase
          variant={availableNow ? "soft" : "solid"}
          tone={availableNow ? "secondary" : "inverse"}
        >
          {availabilityHint}
        </Badge>
      )}
      {card.footnote && (
        <Badge size="inherit" weight="semibold">
          {card.footnote}
        </Badge>
      )}
    </motion.div>
  );
}

interface ClampFadeProps {
  size: StorefrontSizeToken;
  title: string;
  onExpand?: () => void;
}

/** Bottom fade plus maximize chip over a clamped body; decorative without `onExpand`. */
function ClampFade({ size, title, onExpand }: ClampFadeProps) {
  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-fade bg-gradient-to-t from-card to-card/0"
        style={{ fontSize: `${RAIL_CTA_REM[size]}rem` }}
      />
      {onExpand ? (
        <Button
          variant="chip"
          tone="secondary"
          size="icon-xs"
          shape="circle"
          aria-label={`Ver tudo sobre ${title}`}
          onClick={(event) => {
            event.stopPropagation();
            onExpand();
          }}
          className="absolute -bottom-1 right-0"
        >
          <Icon as={ArrowsOutSimple} size="xs" tone="secondary" weight="bold" />
        </Button>
      ) : (
        <Surface
          as="span"
          surface="card"
          aria-hidden="true"
          className="absolute -bottom-1 right-0 flex h-7 w-7 items-center justify-center rounded-full shadow-raised"
        >
          <Icon as={ArrowsOutSimple} size="xs" tone="secondary" weight="bold" />
        </Surface>
      )}
    </>
  );
}

function useClampOverflow() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const clamped = [titleRef.current, bodyRef.current].filter(
      (node): node is HTMLHeadingElement | HTMLDivElement => node !== null,
    );
    const measure = () =>
      setOverflowing(clamped.some((node) => node.scrollHeight > node.clientHeight + 1));

    measure();
    let alive = true;
    document.fonts?.ready.then(() => alive && measure());
    const observer = new ResizeObserver(measure);
    clamped.forEach((node) => observer.observe(node));
    return () => {
      alive = false;
      observer.disconnect();
    };
  }, []);

  return { titleRef, bodyRef, overflowing };
}

interface BenefitCardProps extends BenefitCardStyleProps {
  hidden?: boolean;
  onExpand: (card: BenefitShowcaseCard, origin: HTMLElement) => void;
}

/**
 * Collapsed benefit card: title clamps at two lines and the body at three,
 * fading into the card with a maximize control. Only a card that actually
 * overflows becomes clickable — a short one stays a plain card.
 */
export function BenefitCard({ card, size, rounded, hidden = false, onExpand }: BenefitCardProps) {
  const { availableNow } = useAvailability(card);
  const { titleRef, bodyRef, overflowing } = useClampOverflow();
  const articleRef = useRef<HTMLElement>(null);

  const expand = () => {
    if (overflowing && articleRef.current) onExpand(card, articleRef.current);
  };

  return (
    <Card
      as="article"
      padding="none"
      ref={articleRef}
      data-testid={`benefit-card-${card.kind}`}
      data-available={availableNow ? "true" : "false"}
      data-overflowing={overflowing ? "true" : "false"}
      aria-label={card.title}
      dimmed={!availableNow}
      onClick={expand}
      className={cn("flex w-full flex-col gap-2", overflowing && "cursor-pointer", hidden && "invisible")}
      style={cardSurface(size, rounded)}
    >
      <div className="flex items-center gap-3">
        <BenefitIconChip kind={card.kind} size={size} />
        <Heading
          level={3}
          leading="tight"
          ref={titleRef}
          className="min-w-0 flex-1"
          style={{ fontSize: `${RAIL_NAME_REM[size] * 1.15}rem`, ...lineClamp(COLLAPSED_TITLE_LINES) }}
        >
          {card.title}
        </Heading>
      </div>

      <div className="relative">
        <Text
          as="div"
          ref={bodyRef}
          tone="muted"
          className="[&>p+p]:mt-1"
          style={{ fontSize: `${RAIL_CTA_REM[size]}rem`, ...lineClamp(COLLAPSED_BODY_LINES) }}
        >
          {card.lines.map((line) => (
            <Text key={line} leading="snug">
              {line}
            </Text>
          ))}
        </Text>

        {overflowing && <ClampFade size={size} title={card.title} onExpand={expand} />}
      </div>

      <BenefitChips card={card} size={size} />
    </Card>
  );
}

type SpotlightPhase = "enter" | "open" | "leave";

interface BenefitSpotlightProps extends BenefitCardStyleProps {
  origin: HTMLElement;
  onClosed: () => void;
}

/**
 * Full benefit over a veil. The card itself morphs: it mounts exactly over its
 * collapsed twin, grows to the centred box and, on close, shrinks back to the
 * twin's live position before unmounting — one element, never a new card.
 */
export function BenefitSpotlight({ card, size, rounded, origin, onClosed }: BenefitSpotlightProps) {
  const reduceMotion = useReducedMotion();
  const { availableNow } = useAvailability(card);
  const [phase, setPhase] = useState<SpotlightPhase>(reduceMotion ? "open" : "enter");
  const [anchor, setAnchor] = useState(() => origin.getBoundingClientRect());
  const leaving = useRef(false);
  const { zIndex, backdropZIndex } = useModalLayer(true);
  useLockBodyScroll(true);

  useEffect(() => {
    if (phase !== "enter") return;
    const frame = requestAnimationFrame(() => setPhase("open"));
    return () => cancelAnimationFrame(frame);
  }, [phase]);

  useEffect(() => {
    if (phase !== "leave") return;
    const timer = window.setTimeout(onClosed, LEAVE_FALLBACK_MS);
    return () => window.clearTimeout(timer);
  }, [phase, onClosed]);

  const close = useCallback(() => {
    if (leaving.current) return;
    leaving.current = true;
    if (reduceMotion) {
      onClosed();
      return;
    }
    setAnchor(origin.getBoundingClientRect());
    setPhase("leave");
  }, [origin, onClosed, reduceMotion]);

  const docked = phase !== "open";
  const transition = reduceMotion ? REDUCED_MOTION : MORPH_TRANSITION;
  const placement: CSSProperties = docked
    ? {
        position: "fixed",
        top: anchor.top,
        left: anchor.left,
        width: anchor.width,
        height: anchor.height,
      }
    : { position: "relative", width: "min(34rem, 100%)", maxHeight: "85dvh" };

  return (
    <Dialog.Root modal={false} open onOpenChange={(open) => !open && close()}>
      <Dialog.Portal>
        <MotionBackdrop
          zIndex={backdropZIndex}
          onClick={close}
          initial={{ opacity: 0 }}
          animate={{ opacity: docked ? 0 : 1 }}
          transition={reduceMotion ? REDUCED_MOTION : TRANSITION.enter}
        />
        <div
          className="pointer-events-none fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex }}
        >
          <Dialog.Content
            asChild
            aria-describedby={undefined}
            onInteractOutside={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              origin.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
            }}
          >
            <MotionSurface
              surface="card"
              ink
              as="article"
              layout
              transition={transition}
              initial={false}
              animate={{
                boxShadow: docked ? MORPH_SHADOW.rest : MORPH_SHADOW.lifted,
                opacity: docked && !availableNow ? UNAVAILABLE_OPACITY : 1,
              }}
              onLayoutAnimationComplete={() => {
                if (phase === "leave") onClosed();
              }}
              data-testid={`benefit-spotlight-${card.kind}`}
              className={cn(
                "pointer-events-auto flex flex-col gap-2 overflow-hidden shadow-card outline-none",
                !availableNow && "grayscale",
              )}
              style={{ ...cardSurface(size, rounded), ...placement }}
            >
              <motion.div
                layout="position"
                transition={transition}
                className={cn("flex items-center gap-3", !docked && "pr-8")}
              >
                <BenefitIconChip kind={card.kind} size={size} />
                <Dialog.Title asChild>
                  <Heading
                    level={2}
                    leading="tight"
                    className="min-w-0 flex-1"
                    style={{
                      fontSize: `${RAIL_NAME_REM[size] * 1.15}rem`,
                      ...(docked ? lineClamp(COLLAPSED_TITLE_LINES) : {}),
                    }}
                  >
                    {card.title}
                  </Heading>
                </Dialog.Title>
              </motion.div>

              <MotionButton
                variant="ghost"
                tone="neutral"
                size="auto"
                shape="circle"
                aria-label="Fechar"
                onClick={close}
                layout="position"
                initial={false}
                animate={{ opacity: docked ? 0 : 1, scale: docked ? 0.6 : 1 }}
                transition={
                  reduceMotion
                    ? REDUCED_MOTION
                    : { ...transition, opacity: { duration: DURATION.base }, scale: { duration: DURATION.base } }
                }
                className="absolute right-2 top-2 h-8 w-8 justify-center"
              >
                <Icon as={Close} size="sm" weight="bold" />
              </MotionButton>

              <motion.div
                layout="position"
                transition={transition}
                className={cn("relative", !docked && "flex min-h-0 flex-1 flex-col")}
              >
                <Text
                  as="div"
                  tone="muted"
                  className={cn("[&>p+p]:mt-1", !docked && "min-h-0 overflow-y-auto")}
                  style={{
                    fontSize: `${RAIL_CTA_REM[size]}rem`,
                    ...(docked ? lineClamp(COLLAPSED_BODY_LINES) : {}),
                  }}
                >
                  {card.lines.map((line) => (
                    <Text key={line} leading="snug">
                      {line}
                    </Text>
                  ))}
                </Text>
                <motion.div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  initial={false}
                  animate={{ opacity: docked ? 1 : 0 }}
                  transition={reduceMotion ? REDUCED_MOTION : docked ? FADE_RETURN : FADE_LEAVE}
                >
                  <ClampFade size={size} title={card.title} />
                </motion.div>
              </motion.div>

              <BenefitChips card={card} size={size} layoutTransition={transition} />
            </MotionSurface>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
